import { Autocomplete, Stack, TextField, Typography } from "@mui/material";
import TrendCard from "components/common/TrendCard";
import { useSelector } from "react-redux";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";

const NONE_KEY = "__NONE__";

const BlankChart = ({ blankChartMessage }) => {
  return (
    <Stack
      sx={{
        width: "100%",
        height: "150px",
        borderRadius: "0.5rem",
        border: "1px solid gainsboro",
      }}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Typography
        padding={8}
        textAlign={"center"}
        variant={"body2"}
        color={"silver !important"}
      >
        {blankChartMessage}
      </Typography>
    </Stack>
  );
};

const ChartedExposureSelector = ({
  value,
  onChange,
  hideInput,
  hideChart,
  fullWidth,
  borderColour,
  blankChartMessage = "Please enter one or more data points",
  label = "Exposure",
  TextFieldProps,
}) => {
  const exposures = useSelector(exposureSelectors.getExposureList);
  const data = useSelector(exposureSelectors.getData);

  const noneOption = { key: NONE_KEY, name: "-- None --" };
  const options = [noneOption, ...exposures];

  const selectedExposure = (exposures ?? []).find(({ key }) => key === value);

  let trendData = {
    name: selectedExposure?.name,
    values: [],
  };

  let yearLabels = [];
  let yearRangeLabel;

  if (selectedExposure != null && data != null) {
    const years = Object.keys(data).sort((a, b) => a - b);
    years.forEach((year) => {
      const metric = Object.entries(data[year]).filter(
        ([k, _]) => k === selectedExposure.key
      )?.[0]?.[1];
      if (metric != null) {
        yearLabels.push(year);
        trendData.values.push(metric);
      }
    });
    const enDashChar = "\u2013";
    yearRangeLabel = `${yearLabels[0]}${enDashChar}${
      yearLabels[yearLabels.length - 1]
    }`;
  }

  return (
    <Stack direction={"column"}>
      {!hideInput && (
        <Autocomplete
          value={
            options.find(({ key }) => key === (value ?? NONE_KEY)) ?? noneOption
          }
          onChange={(_, newValue) => {
            const newKey = newValue?.key ?? NONE_KEY;
            onChange(newKey === NONE_KEY ? null : newKey);
          }}
          options={options}
          getOptionKey={(option) => option.key}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              type={"text"}
              variant={"filled"}
              label={label}
              InputLabelProps={{ shrink: true }}
              fullWidth
              {...TextFieldProps}
            />
          )}
        />
      )}
      {!hideChart && (
        <Stack marginTop={hideInput ? 0 : 2}>
          {trendData.values.length > 1 && (
            <TrendCard
              data={trendData}
              labels={yearLabels}
              borderColour={borderColour}
              secondaryLabel={yearRangeLabel}
              precentLabelAddon={" YoY"}
              showTooltip
              fullWidth={fullWidth}
            />
          )}
          {trendData.values.length === 1 && value != null && (
            <Stack marginLeft={1.5}>
              <Typography variant={"subtitle2"}>
                {selectedExposure?.name}
              </Typography>
              <Typography variant={"h6"}>
                {(trendData.values[0] ?? 0).toLocaleString("en-US")}
              </Typography>
            </Stack>
          )}
          {trendData.values.length < 1 && value != null && (
            <BlankChart blankChartMessage={blankChartMessage} />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ChartedExposureSelector;
